import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useState } from "react";
import { FileText, PlayCircle, Download, ExternalLink, ChevronDown, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/resources")({
  head: () => ({
    meta: [
      ...routeMeta("/resources"),
      { title: "Tax & Accounting Resources | CCOB And Company PA" },
      { name: "description", content: "Tax guidance, financial planning tips, IRS resources, refund tracking, and downloadable guides from CCOB And Company PA." },
      { property: "og:title", content: "Resources — CCOB And Company PA" },
      { property: "og:description", content: "Track your refund, download tax checklists, and explore CPA-curated guides." },
    ],
    links: canonicalLinks("/resources"),
  }),
  component: ResourcesPage,
});

const articles = [
  { t: "Year-End Tax Planning Checklist", d: "Practical steps to lower your tax burden before December 31." },
  { t: "Small Business Bookkeeping Basics", d: "The minimum monthly cadence every owner should follow." },
  { t: "Understanding IRS Notices", d: "What each common letter means — and what to do next." },
  { t: "Estimated Quarterly Taxes", d: "Who needs to pay them, when they're due, and how to calculate." },
];

const faqs = [
  { q: "How long does it take to prepare a tax return?", a: "Most individual returns are completed within 5–10 business days once all documents are received." },
  { q: "Do you offer virtual consultations?", a: "Yes — by phone or video. We also welcome you in our Winter Garden office." },
  { q: "Can you represent me before the IRS?", a: "Absolutely. As a CPA firm, we represent clients in audits, notices, and resolution matters." },
];

function ResourcesPage() {
  const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
  const [open, setOpen] = useState<number | null>(0);
  return (
    <>
      <PageHero eyebrow="Resources" title="Knowledge to Help You Stay Ahead" subtitle="Curated tax updates, planning tips, and tools — including refund tracking and downloadable guides." image={img.heroResources} />

      {/* Track refund */}
      <section className="py-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Tax Tools</div>
              <h2 className="font-display text-4xl">Track Your Refund</h2>
              <div className="gold-line mt-5" />
              <p className="mt-5 opacity-90">Check the status of your federal refund directly with the IRS. Have your SSN, filing status, and refund amount ready.</p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <a href="https://www.irs.gov/refunds" target="_blank" rel="noreferrer" className="btn-gold">IRS Refund Tracker <ExternalLink className="h-4 w-4" /></a>
              <Link to="/calculators" className="btn-outline text-white">Financial Calculators</Link>
              <a href="#checklist" className="btn-outline text-white">Tax Preparation Checklist</a>
              <Link to="/contact" hash="consultation" className="btn-outline text-white">Schedule Consultation</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles */}
      <section className="pb-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="mb-12">
            <h2 className="font-display text-4xl text-primary">Guides & Articles</h2>
            <div className="gold-line mt-5" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {articles.map((a) => (
              <motion.article key={a.t} {...fadeUp} className="bg-card border border-border rounded-2xl p-6 hover:border-gold transition">
                <FileText className="h-6 w-6 text-gold" />
                <h3 className="font-display text-lg text-primary mt-4">{a.t}</h3>
                <p className="text-sm text-muted-foreground mt-2">{a.d}</p>
                <Link to="/blog" className="inline-flex items-center gap-2 mt-4 text-sm text-primary hover:text-gold-text">Read our tax and accounting insights <ArrowRight className="h-4 w-4" /></Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Videos */}
      <section className="pb-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="mb-12">
            <h2 className="font-display text-4xl text-primary">Educational Videos</h2>
            <div className="gold-line mt-5" />
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {["Tax Filing Basics","Small Business Accounting","IRS Audit Walk-Through"].map((t) => (
              <motion.div key={t} {...fadeUp} className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="aspect-video bg-primary/10 grid place-items-center"><PlayCircle className="h-14 w-14 text-primary" /></div>
                <div className="p-5"><div className="font-display text-lg text-primary">{t}</div></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Downloads */}
      <section id="checklist" className="pb-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="mb-12">
            <h2 className="font-display text-4xl text-primary">Downloadable Guides</h2>
            <div className="gold-line mt-5" />
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {["Tax Preparation Checklist","New Business Setup Guide","Year-End Bookkeeping Checklist"].map((t) => (
              <motion.a key={t} {...fadeUp} href="#" className="bg-card border border-border rounded-2xl p-6 flex items-start gap-4 hover:border-gold transition">
                <div className="h-11 w-11 rounded-full bg-secondary text-primary grid place-items-center"><Download className="h-5 w-5" /></div>
                <div>
                  <div className="font-display text-lg text-primary">{t}</div>
                  <div className="text-sm text-muted-foreground mt-1">PDF · Free</div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Quick FAQ */}
      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <motion.h2 {...fadeUp} className="font-display text-4xl text-primary">Quick Answers</motion.h2>
          <div className="gold-line mt-5" />
          <div className="mt-8 divide-y divide-border border border-border rounded-2xl bg-card">
            {faqs.map((f, i) => (
              <button key={f.q} onClick={() => setOpen(open === i ? null : i)} className="w-full text-left p-5 flex items-center justify-between gap-4 hover:bg-secondary/40 transition">
                <div>
                  <div className="font-medium text-primary">{f.q}</div>
                  {open === i && <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>}
                </div>
                <ChevronDown className={`h-5 w-5 text-gold transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
